import * as React from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '../../../shared/Icon';
import { IScoreboardBranding } from '../types';

interface BrandingSectionProps {
  branding: IScoreboardBranding;
  uploadingLogo: boolean;
  uploadError: string | null;
  onClearUploadError: () => void;
  onUpdate: (updates: Partial<IScoreboardBranding>) => void;
  onLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const BrandingSection = ({
  branding,
  uploadingLogo,
  uploadError,
  onClearUploadError,
  onUpdate,
  onLogoUpload,
}: BrandingSectionProps): React.ReactElement => {
  const handleClearLogo = (): void => {
    onUpdate({ logoUrl: null, selectedLogoId: null });
  };

  return (
    <Box role='group' aria-labelledby='branding-section-label'>
      <Text
        id='branding-section-label'
        fontSize='xs'
        fontWeight={700}
        textTransform='uppercase'
        letterSpacing='wider'
        color='var(--scoreboard-text-secondary, #b0b0b0)'
        mb={3}
      >
        Branding
      </Text>
      <VStack gap={3} align='stretch'>
        <Box>
          <Text
            id='custom-title-label'
            fontSize='sm'
            color='var(--scoreboard-text-secondary, #b0b0b0)'
            mb={1}
          >
            Custom Title
          </Text>
          <Input
            value={branding.customTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              onUpdate({ customTitle: e.target.value })
            }
            placeholder='Scoreboard'
            size='sm'
            bg='rgba(255, 255, 255, 0.05)'
            borderColor='rgba(255, 255, 255, 0.2)'
            color='var(--scoreboard-text-primary, #ffffff)'
            aria-labelledby='custom-title-label'
          />
        </Box>

        {/* Logo */}
        <Box>
          <Flex align='center' justify='space-between' mb={2}>
            <Text
              id='logo-section-label'
              fontSize='sm'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
            >
              Logo
            </Text>
            <Flex gap={2}>
              {branding.logoUrl && (
                <Button
                  size='xs'
                  variant='outline'
                  color='var(--scoreboard-text-secondary, #b0b0b0)'
                  borderColor='rgba(255, 255, 255, 0.2)'
                  _hover={{ bg: 'rgba(255, 0, 0, 0.2)' }}
                  onClick={handleClearLogo}
                  aria-label='Clear logo'
                >
                  <Icon name='close' />
                  <Text ml={1}>Clear</Text>
                </Button>
              )}
              <Button
                as='label'
                size='xs'
                variant='outline'
                cursor='pointer'
                color='var(--scoreboard-text-secondary, #b0b0b0)'
                borderColor='rgba(255, 255, 255, 0.2)'
                _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                disabled={uploadingLogo}
                aria-label={uploadingLogo ? 'Uploading logo' : 'Upload logo'}
              >
                <Icon name='add' />
                <Text ml={1}>{uploadingLogo ? 'Loading...' : 'Upload'}</Text>
                <input
                  type='file'
                  accept='image/*'
                  onChange={onLogoUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingLogo}
                  aria-label='Select logo image file'
                />
              </Button>
            </Flex>
          </Flex>

          {branding.logoUrl ? (
            <Box
              w='80px'
              h='80px'
              borderRadius='md'
              border='2px solid var(--scoreboard-primary, #1a73e8)'
              overflow='hidden'
              bg='rgba(255, 255, 255, 0.05)'
            >
              <img
                src={branding.logoUrl}
                alt='Scoreboard logo'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: '0.25rem',
                }}
              />
            </Box>
          ) : (
            <Text
              fontSize='xs'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
            >
              No logo set. Upload an image to display in the header.
            </Text>
          )}
          <Text
            fontSize='xs'
            color='var(--scoreboard-text-secondary, #b0b0b0)'
            mt={2}
            fontStyle='italic'
          >
            Uploading a new image will replace the current logo.
          </Text>
          {uploadError && (
            <Flex align='center' gap={2} mt={2}>
              <Text fontSize='xs' color='red.400'>
                {uploadError}
              </Text>
              <Button
                size='xs'
                variant='ghost'
                color='red.400'
                minW='auto'
                h='auto'
                p={0}
                onClick={onClearUploadError}
                aria-label='Dismiss upload error'
              >
                <Icon name='close' />
              </Button>
            </Flex>
          )}
        </Box>

        <Flex align='center' justify='space-between'>
          <Text fontSize='sm' color='var(--scoreboard-text-secondary, #b0b0b0)'>
            Show Organization Logo
          </Text>
          <Switch.Root
            checked={branding.showOrganizationLogo}
            onCheckedChange={(details: { checked: boolean }): void =>
              onUpdate({ showOrganizationLogo: details.checked })
            }
            aria-label='Show organization logo'
          >
            <Switch.HiddenInput />
            <Switch.Control
              bg='rgba(255, 255, 255, 0.1)'
              _checked={{ bg: 'var(--scoreboard-primary, #1a73e8)' }}
            >
              <Switch.Thumb bg='white' />
            </Switch.Control>
          </Switch.Root>
        </Flex>
      </VStack>
    </Box>
  );
};
